import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, CalendarDays, NotebookPen, Activity, ArrowUpRight,
  Clock, Video, MapPin, UserPlus, ChevronRight, CheckCircle2, Circle, 
  ClipboardList, Building, Sparkles, X
} from "lucide-react";
import { dashboardStats, listPlanRequests, processPlanRequest, addPatientCredits } from "@/lib/db";
import StatsCard from "@/components/common/StatsCard";
import PageHeader from "@/components/common/PageHeader";
import Avatar from "@/components/common/Avatar";
import { AppointmentStatusBadge } from "@/components/common/StatusBadge";
import { formatTime, formatDate } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";

export default function Dashboard() {
  const { profile, clinicId, clinic } = useAuth();
  const myRole = clinic?.my_role || "professional";
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissedOnboarding, setDismissedOnboarding] = useState(
    localStorage.getItem(`dismissed_onboarding_${profile?.id}`) === "true"
  );

  const handleDismiss = () => {
    localStorage.setItem(`dismissed_onboarding_${profile?.id}`, "true");
    setDismissedOnboarding(true);
  };

  const load = async () => {
    setLoading(true);
    try {
      setStats(await dashboardStats(clinicId, myRole));
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [clinicId]);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Bom dia";
    if (h < 18) return "Boa tarde";
    return "Boa noite";
  })();

  const firstName = profile?.full_name?.split(" ")[0] || "";

  return (
    <div className="ds-fade-up">
      <div className="mb-8">
        <PageHeader
          eyebrow={greeting}
          title={firstName ? `${firstName}, aqui está sua clínica hoje` : "Bem-vinda(o)"}
          description={`${formatDate(new Date().toISOString(), { weekday: "long" })} · acompanhe seus pacientes, agenda e evolução clínica em um só lugar.`}
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 ds-stagger">
        <StatsCard testid="stat-today-appointments" label="Agendamentos hoje" value={loading ? "…" : (stats?.today_appointments ?? 0)} icon={CalendarDays} accent="brand" hint="Próximos 24 horas" to="/app/agenda" />
        <StatsCard testid="stat-active-patients"   label="Pacientes ativos"    value={loading ? "…" : (stats?.active_patients ?? 0)}   icon={Users} accent="info" hint={`de ${stats?.total_patients ?? 0} total`} to="/app/pacientes" />
        
        {(myRole !== 'professional' || !clinicId) && (
          <StatsCard testid="stat-pending-links"     label="Vínculos pendentes"  value={loading ? "…" : (stats?.pending_links ?? 0)}     icon={UserPlus} accent="warning" hint="Aguardando aprovação" to="/app/vinculacoes" />
        )}
        
        <StatsCard testid="stat-week-appointments" label="Agenda da semana"    value={loading ? "…" : (stats?.week_appointments ?? 0)} icon={Activity} accent="success" hint="Próximos 7 dias" to="/app/agenda" />
      </div>

      {/* Show requests for any clinic staff (admin, professional, receptionist) or solo professional */}
      {(myRole === 'admin' || myRole === 'professional' || myRole === 'receptionist' || !clinicId) && (
        <PlanRequestsSection clinicId={clinicId} />
      )}

      {/* Onboarding Checklist for new professionals */}
      {!loading && stats?.total_patients === 0 && !dismissedOnboarding && (
        <OnboardingChecklist onboarding={stats?.onboarding} onDismiss={handleDismiss} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 ds-card p-5 md:p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="ds-overline">Agenda</div>
              <h2 className="text-lg font-bold tracking-tight" style={{fontFamily:"'Outfit',sans-serif"}}>Próximos atendimentos</h2>
            </div>
            <Link to="/app/agenda" className="ds-link inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider">
              Ver agenda <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          {loading ? <SkeletonRows /> : (stats?.upcoming_appointments ?? []).length === 0 ? (
            <div className="text-sm text-[hsl(var(--text-tertiary))] py-12 text-center bg-[hsl(var(--bg-base))]/50 rounded-2xl border-2 border-dashed border-[hsl(var(--border-divider))]">
              Sem agendamentos futuros.
            </div>
          ) : (
            <ul className="space-y-3" data-testid="upcoming-list">
              {stats.upcoming_appointments.map((a) => (
                <li key={a.id}>
                  <Link to={`/app/pacientes/${a.patient_id}?tab=evolucoes`} className="p-3 md:p-4 flex items-center gap-3 md:gap-4 rounded-2xl bg-[hsl(var(--bg-base))]/40 border border-transparent hover:border-[hsl(var(--brand-light))] hover:bg-white transition-all group">
                    <div className="w-14 text-center shrink-0 border-r border-[hsl(var(--border-divider))] pr-3 md:pr-4">
                      <div className="text-[10px] uppercase font-bold text-[hsl(var(--text-tertiary))]">{formatDate(a.start_at, { day: "2-digit", month: "short" }).replace(".","")}</div>
                      <div className="text-sm md:text-base font-bold text-[hsl(var(--brand))]" style={{fontFamily:"'Outfit',sans-serif"}}>{formatTime(a.start_at)}</div>
                    </div>
                    <Avatar name={a.patient_name} size={40} className="shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold truncate group-hover:text-[hsl(var(--brand))] transition-colors">{a.patient_name}</div>
                      <div className="text-[11px] text-[hsl(var(--text-tertiary))] flex items-center gap-1.5 mt-0.5">
                        <span className="inline-flex items-center gap-1">
                          {a.modality === "online" ? <Video className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                          {a.modality === "online" ? "Online" : "Presencial"}
                        </span>
                        <span className="opacity-30">·</span>
                        <span className="truncate">{a.type}</span>
                      </div>
                    </div>
                    <div className="shrink-0 scale-90 md:scale-100">
                      <AppointmentStatusBadge status={a.status} />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="ds-card p-5 md:p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="ds-overline">Pacientes</div>
              <h2 className="text-lg font-bold tracking-tight" style={{fontFamily:"'Outfit',sans-serif"}}>Recentes</h2>
            </div>
            <Link to="/app/pacientes" className="ds-link inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider">
              Ver todos <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          {loading ? <SkeletonRows /> : (stats?.recent_patients ?? []).length === 0 ? (
            <div className="text-sm text-[hsl(var(--text-tertiary))] py-12 text-center bg-[hsl(var(--bg-base))]/50 rounded-2xl border-2 border-dashed border-[hsl(var(--border-divider))]">
              Nenhum paciente ainda. <br />
              <Link to="/app/convites" className="ds-link mt-2 inline-block font-bold">Convide o primeiro</Link>
            </div>
          ) : (
            <ul className="space-y-1" data-testid="recent-patients-list">
              {stats.recent_patients.slice(0, 5).map((p) => (
                <li key={p.id}>
                  <Link to={`/app/pacientes/${p.id}`} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-[hsl(var(--bg-base))] transition-colors group">
                    <Avatar name={p.full_name} size={36} className="shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold truncate group-hover:text-[hsl(var(--brand))] transition-colors">{p.full_name}</div>
                      <div className="text-[11px] text-[hsl(var(--text-tertiary))]">Em acompanhamento</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[hsl(var(--text-tertiary))] opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="ds-card mt-8 p-6 bg-gradient-to-br from-[hsl(var(--brand))] to-[hsl(var(--brand-dark))] text-white border-none relative overflow-hidden group shadow-xl shadow-[hsl(var(--brand))]/20">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl group-hover:scale-150 transition-transform duration-700" />
        <div className="relative flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <div className="text-white/80 text-[10px] uppercase font-bold tracking-widest mb-1">Dica clínica</div>
            <h3 className="text-lg font-bold mb-2 text-white" style={{fontFamily:"'Outfit',sans-serif"}}>Padrão SOAP de Evolução</h3>
            <p className="text-sm text-white font-medium leading-relaxed max-w-2xl">
              Mantenha a consistência usando o modelo <strong>SOAP</strong> (Subjetivo · Objetivo · Avaliação · Plano).
              Isso facilita a continuidade do cuidado e protege o seu exercício profissional.
            </p>
          </div>
          <Link to="/app/sessoes" className="ds-btn-secondary bg-white text-[hsl(var(--brand))] hover:bg-white/90 border-none px-6 shadow-lg shadow-black/10 transition-transform hover:scale-105 active:scale-95">
            Ver todas as sessões
          </Link>
        </div>
      </div>
    </div>
  );
}

function PlanRequestsSection({ clinicId }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listPlanRequests(clinicId);
      // Filtro menos restritivo para debug
      const pending = (data || []).filter(r => r.status === "pending");
      setRequests(pending);
    } catch (e) {
      console.error("Erro ao carregar solicitações:", e);
      setError(e.message || "Erro de conexão com o banco");
    } finally { setLoading(false); }
  };
  
  useEffect(() => { load(); }, [clinicId]);

  const handleAction = async (req, action) => {
    try {
      if (action === "approved") {
        await addPatientCredits(req.patient_id, req.plan?.credits_amount || 0, `Plano: ${req.plan?.name}`, 30, clinicId);
      }
      await processPlanRequest(req.id, action);
      load();
    } catch (e) {
      console.error("Erro completo:", e);
      alert("Erro ao processar solicitação: " + (e.message || e));
    }
  };

  if (loading) return (
    <div className="mt-7 ds-card p-6 flex items-center justify-center border border-dashed border-[hsl(var(--border-default))]">
      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[hsl(var(--brand))]"></div>
      <span className="ml-3 text-sm text-[hsl(var(--text-secondary))]">Buscando solicitações...</span>
    </div>
  );

  if (error) return (
    <div className="mt-7 ds-card p-6 border-l-4 border-[hsl(var(--danger-text))] bg-red-50">
      <div className="text-sm font-bold text-[hsl(var(--danger-text))]">Erro ao carregar pedidos:</div>
      <div className="text-xs text-red-600">{error}</div>
      <button onClick={load} className="mt-2 text-xs underline">Tentar novamente</button>
    </div>
  );

  return (
    <div className="mt-7 ds-card p-6 border-l-4 border-[hsl(var(--warning-text))] shadow-md">
      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="ds-overline">Atenção Necessária</div>
          <h2 className="text-lg font-semibold tracking-tight" style={{fontFamily:"'Outfit',sans-serif"}}>Solicitações de Crédito</h2>
        </div>
        {requests.length > 0 && (
          <span className="bg-[hsl(var(--warning-text))] text-white text-[10px] font-bold px-2 py-0.5 rounded-full animate-bounce">
            {requests.length} NOVO(S)
          </span>
        )}
      </div>

      {requests.length === 0 ? (
        <div className="py-4 text-center text-sm text-[hsl(var(--text-tertiary))] italic border border-dashed border-[hsl(var(--border-default))] rounded-xl">
          Nenhuma solicitação de plano pendente.
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map(req => (
            <div key={req.id} className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-[hsl(var(--bg-base))] rounded-xl gap-4 border border-[hsl(var(--border-default))] hover:border-[hsl(var(--brand))] transition-colors">
              <div className="flex items-center gap-3">
                <Avatar name={req.patient?.full_name || "Paciente"} size={40} />
                <div>
                  <div className="font-semibold text-sm">{req.patient?.full_name || "Paciente Pendente"}</div>
                  <div className="text-xs text-[hsl(var(--text-tertiary))]">
                    Solicitou: <span className="font-bold text-[hsl(var(--brand))]">{req.plan?.name || "Plano Selecionado"}</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleAction(req, "rejected")} className="ds-btn-ghost text-xs text-[hsl(var(--danger-text))]">Recusar</button>
                <button onClick={() => handleAction(req, "approved")} className="ds-btn-primary text-xs">Aprovar e Liberar Créditos</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function OnboardingChecklist({ onboarding, onDismiss }) {
  const steps = [
    { 
      id: "profile", 
      title: "Complete seu perfil profissional", 
      desc: "Adicione seu CRP e biografia para os pacientes te conhecerem.", 
      done: onboarding?.hasProfile,
      link: "/app/configuracoes?tab=profile"
    },
    { 
      id: "availability", 
      title: "Defina seus horários de atendimento", 
      desc: "Configure sua grade semanal para agendamentos online.", 
      done: onboarding?.hasAvailability,
      link: "/app/configuracoes?tab=availability"
    },
    { 
      id: "clinic", 
      title: "Crie sua clínica virtual", 
      desc: "Personalize sua marca e prepare-se para gerenciar sua equipe.", 
      done: onboarding?.hasClinic,
      link: "/app/clinica"
    },
    { 
      id: "patients", 
      title: "Adicione seu primeiro paciente", 
      desc: "Convide seus pacientes atuais para a plataforma.", 
      done: false, // will be done when total_patients > 0 (handled by exclusion)
      link: "/app/pacientes"
    },
  ];

  const doneCount = steps.filter(s => s.done).length;
  const progress = (doneCount / steps.length) * 100;

  return (
    <div className="mt-8 ds-fade-up">
      <div className="ds-card p-6 border-none bg-gradient-to-br from-[hsl(var(--bg-base))] to-[hsl(var(--brand-light))]/10 shadow-xl shadow-brand/5 border border-[hsl(var(--brand-light))]/20">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[hsl(var(--brand-light))] text-[hsl(var(--brand))] text-[10px] font-bold uppercase tracking-wider mb-3">
              <Sparkles className="w-3 h-3" /> Começando com o pé direito
            </div>
            <h2 className="text-2xl font-bold tracking-tight" style={{fontFamily:"'Outfit',sans-serif"}}>Vamos configurar sua clínica?</h2>
            <p className="text-sm text-[hsl(var(--text-secondary))] mt-1">Complete estas etapas para começar a atender seus pacientes com excelência.</p>
          </div>
          <div className="flex items-center gap-4 bg-white/60 p-4 rounded-2xl border border-white relative">
            <button 
              onClick={onDismiss}
              title="Remover este card"
              className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-white border border-[hsl(var(--border-default))] shadow-sm flex items-center justify-center text-[hsl(var(--text-tertiary))] hover:text-red-500 hover:border-red-200 transition-colors z-10"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="text-right">
              <div className="text-xs font-bold text-[hsl(var(--text-tertiary))] uppercase tracking-tighter">Progresso</div>
              <div className="text-2xl font-black text-[hsl(var(--brand))]" style={{fontFamily:"'Outfit',sans-serif"}}>{Math.round(progress)}%</div>
            </div>
            <div className="w-12 h-12 rounded-full border-4 border-[hsl(var(--bg-hover))] relative flex items-center justify-center">
              <div 
                className="absolute inset-0 rounded-full border-4 border-[hsl(var(--brand))] transition-all duration-1000"
                style={{ clipPath: `inset(0 0 ${100 - progress}% 0)` }}
              />
              <ClipboardList className="w-5 h-5 text-[hsl(var(--brand))]" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {steps.map((step) => (
            <Link 
              key={step.id} 
              to={step.link}
              className={`p-4 rounded-2xl border transition-all flex items-start gap-4 group ${
                step.done 
                  ? "bg-white/40 border-transparent opacity-70" 
                  : "bg-white border-[hsl(var(--border-default))] hover:border-[hsl(var(--brand-light))] hover:shadow-lg hover:shadow-brand/5 hover:-translate-y-0.5"
              }`}
            >
              <div className={`mt-0.5 shrink-0 transition-colors ${step.done ? "text-green-500" : "text-[hsl(var(--text-tertiary))] group-hover:text-[hsl(var(--brand))]"}`}>
                {step.done ? <CheckCircle2 className="w-6 h-6" /> : <Circle className="w-6 h-6" />}
              </div>
              <div>
                <h4 className={`text-sm font-bold tracking-tight ${step.done ? "line-through text-[hsl(var(--text-tertiary))]" : "text-[hsl(var(--text-primary))]"}`}>
                  {step.title}
                </h4>
                <p className="text-xs text-[hsl(var(--text-tertiary))] leading-tight mt-1">{step.desc}</p>
              </div>
              {!step.done && (
                <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                  <ChevronRight className="w-4 h-4 text-[hsl(var(--brand))]" />
                </div>
              )}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-3">
      {[0,1,2].map(i => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[hsl(var(--bg-hover))] animate-pulse" />
          <div className="flex-1 h-3 rounded bg-[hsl(var(--bg-hover))] animate-pulse" />
          <div className="w-16 h-3 rounded bg-[hsl(var(--bg-hover))] animate-pulse" />
        </div>
      ))}
    </div>
  );
}
